/**
 * P0-13 · Custom next/image loader for the MinIO/CDN media origin.
 *
 * Self-hosted deploys don't get Vercel's optimizer, so `next/image` routes
 * through this loader instead: it appends transform params (`w`, `q`,
 * `format=auto`) that the image proxy fronting the bucket (imgproxy/thumbor or
 * an on-the-fly resize route) understands. Format negotiation (AVIF → WebP →
 * original) happens at that origin via `format=auto` + the request's Accept
 * header. Registered globally in next.config.js (`images.loaderFile`) — every
 * `next/image` (incl. `<StoreImage>`) routes through it automatically. Do NOT
 * pass it as a `loader` prop from a Server Component: a function prop can't
 * cross to `next/image` (a Client Component) and crashes the render.
 *
 * Client-bundled: keep this dependency- and env-free. Media `src` values are
 * absolute URLs (built server-side by `getPublicUrl` from MEDIA_CDN_BASE_URL);
 * anything else (local /public assets, data URLs, SVGs) passes through
 * untouched — no proxy sits in front of those.
 */

export const IMAGE_OPT_DEFAULT_QUALITY = 75;

export interface ImageLoaderParams {
  src: string;
  width: number;
  quality?: number;
}

/**
 * SR-08 · The widths the imgproxy presets exist for — exactly next.config.js's
 * `images.imageSizes` ∪ `images.deviceSizes` (a test pins the two together).
 * The proxy is started with `IMGPROXY_ONLY_PRESETS=true` and one preset per
 * width (`w64` … `w2048`, WebP q75), so it cannot be asked for anything else.
 */
export const IMAGE_PROXY_WIDTHS = [64, 96, 128, 256, 384, 640, 750, 828, 1080, 1200, 1920, 2048] as const;

/** The smallest preset that is at least `width` wide; the largest if none is. */
export function imageProxyPreset(width: number): string {
  const w = IMAGE_PROXY_WIDTHS.find((p) => p >= width) ?? IMAGE_PROXY_WIDTHS[IMAGE_PROXY_WIDTHS.length - 1];
  return `w${w}`;
}

/**
 * SR-08 · Self-hosted resizing. When `NEXT_PUBLIC_IMAGE_PROXY_PATH` is set at
 * BUILD time (e.g. `/img/insecure`), a media URL of the shape
 * `https://<media host>/<bucket>/brands/…` is rewritten to the imgproxy on the
 * same host: `https://<media host>/img/insecure/w384/plain/brands/…`. The
 * bucket segment is dropped because the proxy's base URL already names it.
 * Unset → the P0-13 behaviour below, unchanged. Quality is fixed by the preset
 * (75); a `quality` prop is ignored on this path.
 *
 * Build-time is right here and wrong in the shared template: this repo's image
 * serves one site, so there is no cross-site value to leak.
 */
function proxied(src: string, width: number): string | null {
  return proxiedWithPreset(src, imageProxyPreset(width));
}

function proxiedWithPreset(src: string, preset: string): string | null {
  const prefix = process.env.NEXT_PUBLIC_IMAGE_PROXY_PATH;
  if (!prefix) return null;
  const url = new URL(src);
  const m = /^\/[^/]+\/(brands\/.+)$/.exec(url.pathname);
  if (!m) return null;
  const base = prefix.replace(/\/+$/, "");
  return `${url.origin}${base}/${preset}/plain/${m[1]}`;
}

export default function imageLoader({
  src,
  width,
  quality,
}: ImageLoaderParams): string {
  // Vectors scale losslessly and data URLs are already inline — no transform.
  if (src.startsWith("data:") || /\.svg(\?|$)/i.test(src)) return src;
  // Relative srcs are app-local static assets, served by Next itself.
  if (!/^https?:\/\//i.test(src)) return src;

  const viaProxy = proxied(src, width);
  if (viaProxy) return viaProxy;

  const url = new URL(src);
  url.searchParams.set("w", String(width));
  url.searchParams.set("q", String(quality ?? IMAGE_OPT_DEFAULT_QUALITY));
  url.searchParams.set("format", "auto");
  return url.toString();
}

/**
 * SR-08 follow-up · A plain `<img>` at a fixed CSS width (a 48 px bundle
 * checkbox thumb, a cart cross-sell card) bypasses `next/image`, so it was
 * fetching the stored original — 2560 px for a 48 px box. This gives it the
 * same loader at 1× and 2× the box. Non-media sources pass through untouched,
 * with no `srcSet`.
 */
export function thumbnailImage(
  src: string,
  cssWidth: number,
): { src: string; srcSet?: string } {
  const one = imageLoader({ src, width: cssWidth });
  if (one === src) return { src };
  const two = imageLoader({ src, width: cssWidth * 2 });
  if (two === one) return { src: one };
  return { src: one, srcSet: `${one} 1x, ${two} 2x` };
}

/**
 * SR-08 follow-up · An `onError` for a resized `<img>`: if the resizer refuses
 * the source (over its megapixel cap, an unsupported format), show the stored
 * original instead of a broken image. Fires once — if the original fails too,
 * the browser shows its broken-image state as it would have before.
 */
export function fallBackToOriginal(original: string) {
  return (e: { currentTarget: HTMLImageElement }) => {
    const img = e.currentTarget;
    if (img.dataset.original === "1" || img.src === original) return;
    img.dataset.original = "1";
    img.removeAttribute("srcset");
    img.src = original;
  };
}

/**
 * The image a transactional email shows for a product: a 128 px JPEG on white
 * from the resizer's `email128` preset. Not the storefront's WebP presets —
 * Outlook for Windows cannot display WebP — and never a stored original, which
 * can be 2048 px for a 64 px cell. Without the resizer (no
 * `NEXT_PUBLIC_IMAGE_PROXY_PATH`), or for a URL outside the bucket, the URL is
 * returned as it is.
 */
export function emailImageUrl(src: string): string {
  if (!/^https?:\/\//i.test(src)) return src;
  return proxiedWithPreset(src, "email128") ?? src;
}
